import { rem, TmElement } from '@thiememeulenhoff/lit-sdk';
import { IconName } from '@thiememeulenhoff/types';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ToasterMessageInterface } from './toaster-message.interface';
import {
  tag,
  toasterMessageStyles,
  ToasterStyles,
} from './toaster-message.styles';
import { ToasterType } from './toaster.types';

@customElement(tag)
export class ToasterMessageComponent
  extends TmElement
  implements ToasterMessageInterface
{
  static override styles = toasterMessageStyles;

  private static currentToastStack: HTMLElement;

  static get toastStack() {
    if (!this.currentToastStack) {
      const stack = document.createElement('a-toaster-stack');
      stack.classList.add('float');
      this.currentToastStack = Object.assign(stack);
      document.body.append(ToasterMessageComponent.toastStack);
    }
    return this.currentToastStack;
  }

  @property({ type: String, reflect: true })
  type: ToasterType = 'neutral';

  @property({ type: String, reflect: true })
  icon: IconName = 'user';

  @property({ type: Boolean, reflect: true })
  dismissible = false;

  @property({ type: Number })
  set duration(duration: number) {
    if (this.#timeOut) {
      clearTimeout(this.#timeOut);
    }

    if (duration) {
      this.#startTime = Date.now();
      this.#duration = duration;
      this.#timeOut = window.setTimeout(
        () => this.dismiss.bind(this)(),
        duration,
      );
    }
  }

  get duration(): number {
    return this.#duration;
  }

  #startTime = 0;

  #duration = 0;

  #timeRemaining = 0;

  #timeOut?: number;

  override render() {
    return html`
      <div class="icon-wrapper">${this.#iconTemplate()}</div>
      <div class="content-wrapper">${this.#contentTemplate()}</div>
    `;
  }

  #iconTemplate() {
    return html`<a-icon
      class="toaster-icon-${this.type}"
      name="${this.icon || ToasterStyles[this.type].icon.name}"
    ></a-icon>`;
  }

  #contentTemplate() {
    return html`
      <div class="header-wrapper">
        <h3 data-test="header"><slot name="header"></slot></h3>
        <button
          class="close-button"
          data-test="close-button"
          @click=${this.dismiss}
        >
          <a-icon name="close"></a-icon>
        </button>
      </div>
      <slot data-test="body" name="body"></slot>
    `;
  }

  toast() {
    this.classList.add('move-in');
    ToasterMessageComponent.toastStack.appendChild(this);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseover', this.#handleHoverEnter);
    this.addEventListener('mouseleave', this.#handleHoverLeave);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseover', this.#handleHoverEnter);
    this.removeEventListener('mouseleave', this.#handleHoverLeave);
  }

  dismiss(event?: Event) {
    event?.stopPropagation();

    this.style.setProperty('--calculated-height', `${rem(this.clientHeight)}`);
    this.classList.add('dismissing');
    this.addEventListener('animationend', this.#animationEnd);
    this.dispatchLocalEvent({ eventName: 'dismissing' });
    this.#timeRemaining = 0;
  }

  #animationEnd = (event: AnimationEvent) => {
    switch (event.animationName) {
      case 'moveRight':
        this.#handleMoveRightEnd();
        break;
      case 'shrink':
        this.#handleShrinkEnd();
        break;
      default:
    }
  };

  #handleMoveRightEnd = () => {
    this.removeEventListener('animationend', this.#handleMoveRightEnd);
    this.classList.add('shrink');
    this.addEventListener('animationend', this.#handleShrinkEnd);
  };

  #handleShrinkEnd = () => {
    this.classList.remove('shrink');
    this.removeEventListener('animationend', this.#handleShrinkEnd);
    this.#dismissed();
  };

  #dismissed() {
    this.dispatchLocalEvent({ eventName: 'dismissed' });
    this.remove();
  }

  #handleHoverEnter() {
    if (this.#timeOut) {
      clearTimeout(this.#timeOut);
      this.#timeOut = undefined;
      const elapsed = Date.now() - this.#startTime;
      this.#timeRemaining = Math.max(this.#duration - elapsed, 0);
    }
  }

  #handleHoverLeave() {
    if (this.#timeRemaining) {
      this.duration = this.#timeRemaining;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toaster-message': ToasterMessageComponent;
  }
}
